'use server';

import { SessionService } from '@/contexts/authentication';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

/**
 * Hand back a link that somebody outside the family can open.
 *
 * Sharing twice gives the same URL — a parent who presses the button again has
 * not made a second secret, and revoking stays one decision about one link.
 */
export async function shareStory(storyId: string): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const userId = await SessionService.getUserId();

    const { getStoryRepository } = await import('@/contexts/stories/repositories');
    const story = await getStoryRepository().getById(storyId);
    if (!story || story.familyId !== familyId) return { success: false, error: 'STORY_NOT_FOUND' };

    const { ensureShareLink } = await import('@/contexts/stories/services/storyShare.server');
    const link = await ensureShareLink({ storyId, familyId, createdBy: userId ?? 'unknown' });
    return { success: true, url: `/s/${link.token}` };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('shareStory', e) };
  }
}

/** Is there a live link, and which? Used by the sidebar row (no link is minted). */
export async function getShareLink(storyId: string): Promise<{ success: boolean; url?: string | null; error?: string }> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { liveShareLink } = await import('@/contexts/stories/services/storyShare.server');
    const link = await liveShareLink(storyId, familyId);
    return { success: true, url: link ? `/s/${link.token}` : null };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('getShareLink', e) };
  }
}

/** Turn every live link to this story off. */
export async function unshareStory(storyId: string): Promise<{ success: boolean; revoked?: number; error?: string }> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { revokeShareLinks } = await import('@/contexts/stories/services/storyShare.server');
    return { success: true, revoked: await revokeShareLinks(storyId, familyId) };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('unshareStory', e) };
  }
}
